export class SearchEngine {
  constructor(config = {}) {
    this.config = {
      minChars: 2,
      caseSensitive: false,
      exactMatch: false,
      debounce: 300,
      ...config
    };
    
    this.searchableElements = new Map();
    this.collectionItems = new Map();
  }

  registerSearchableElement(element, fieldName, list = null) {
    if (!this.searchableElements.has(fieldName)) {
      this.searchableElements.set(fieldName, []);
    }
    this.searchableElements.get(fieldName).push(element);
    
    const collectionItem = element.closest('[pixl-search-item]') || 
                          element.closest('.w-dyn-item') ||
                          element.closest('[role="listitem"]');
    
    if (collectionItem) {
      if (!this.collectionItems.has(collectionItem)) {
        this.collectionItems.set(collectionItem, []);
      }
      this.collectionItems.get(collectionItem).push({
        element: element,
        list: list // Track which list this element belongs to
      });
    }
  }

  search(query, fieldName = null) {
    if (!query || query.length < this.config.minChars) {
      this.showAllItems();
      return { matches: [], hidden: [] };
    }

    const searchQuery = this.config.caseSensitive ? query : query.toLowerCase();
    const matches = [];
    const hidden = [];
    
    // Track match quality for better highlighting
    const matchedItems = new Map();

    for (const [item, elements] of this.collectionItems) {
      let bestMatch = null;
      let bestMatchType = 'none'; // 'exact', 'phrase', 'words', 'none'
      
      for (const elementData of elements) {
        // Handle both old format (direct element) and new format (object with element and list)
        const element = elementData.element || elementData;
        const elementField = element.getAttribute('pixl-search-field');
        
        if (fieldName && elementField !== fieldName) {
          continue;
        }

        const text = this.getElementText(element);
        const searchText = this.config.caseSensitive ? text : text.toLowerCase();
        
        // Strategy 1: Try exact phrase match first
        if (searchText.includes(searchQuery)) {
          bestMatch = { element, text, item, matchType: 'phrase', searchTerm: searchQuery };
          bestMatchType = 'phrase';
          break; // Exact phrase match is best, stop looking
        }
        
        // Strategy 2: If no phrase match, try word-by-word (only for longer queries)
        if (bestMatchType === 'none' && searchQuery.length > 6) {
          const words = searchQuery.split(/\s+/).filter(w => w.length > 2); // Ignore short words like "is", "the"
          const significantWords = words.filter(w => !this.isCommonWord(w));
          
          if (significantWords.length > 0) {
            const foundWords = significantWords.filter(word => searchText.includes(word));
            
            // Only show results if most significant words are found
            if (foundWords.length >= Math.ceil(significantWords.length * 0.7)) {
              bestMatch = { element, text, item, matchType: 'words', searchTerm: foundWords.join(' ') };
              bestMatchType = 'words';
            }
          }
        }
      }

      if (bestMatch) {
        this.showItem(item);
        matches.push(bestMatch);
        matchedItems.set(item, bestMatch);
      } else {
        this.hideItem(item);
        hidden.push(item);
      }
    }

    return { matches, hidden, matchedItems };
  }
  
  isCommonWord(word) {
    const commonWords = ['the', 'is', 'at', 'which', 'on', 'and', 'a', 'to', 'are', 'as', 'was', 'with', 'for', 'his', 'her', 'this', 'that', 'of', 'in', 'or', 'an'];
    return commonWords.includes(word.toLowerCase());
  }

  getElementText(element) {
    return element.textContent || element.innerText || '';
  }

  showItem(item) {
    // Remove all hiding styles and classes
    item.style.opacity = '';
    item.style.visibility = '';
    item.style.height = '';
    item.style.maxHeight = '';
    item.style.overflow = '';
    item.style.pointerEvents = '';
    item.style.transform = '';
    item.style.position = '';
    item.style.transition = '';
    item.classList.remove('pixl-search-hidden');
    item.setAttribute('aria-hidden', 'false');
  }

  hideItem(item) {
    // Special handling for Webflow accordions and interactions
    // Check if this item contains Webflow interaction elements
    const hasWebflowInteractions = item.querySelector('[data-w-id]') || 
                                  item.classList.contains('w-dropdown') ||
                                  item.classList.contains('w-tabs') ||
                                  item.querySelector('.w-dropdown') ||
                                  item.querySelector('.w-tabs');
    
    if (hasWebflowInteractions) {
      // For items with Webflow interactions, use a more gentle hiding approach
      // that doesn't interfere with Webflow's event handling
      item.style.opacity = '0.1'; // Almost invisible but still in DOM
      item.style.maxHeight = '0px';
      item.style.overflow = 'hidden';
      item.style.transition = 'all 0.3s ease';
    } else {
      // For regular items without interactions, use standard hiding
      item.style.transform = 'translateX(-9999px)';
      item.style.opacity = '0';
      item.style.position = 'absolute';
    }
    
    item.classList.add('pixl-search-hidden');
    item.setAttribute('aria-hidden', 'true');
  }

  showAllItems() {
    for (const [item] of this.collectionItems) {
      this.showItem(item);
    }
  }

  clear() {
    this.searchableElements.clear();
    this.collectionItems.clear();
    this.showAllItems();
  }
}